import { useEffect, useState } from "react";
import { Loader2, Users, UserCheck, ClipboardList, FileStack, Hourglass, CheckCircle2, XCircle, Gift, Banknote, IndianRupee } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { StatCard } from "@/components/StatCard";
import { api, inr } from "@/lib/api";

function ChartCard({ title, testid, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid={testid}>
      <h3 className="font-heading font-semibold text-slate-900 text-base">{title}</h3>
      <div className="mt-4 h-56">{children}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/admin/charts").then((r) => setCharts(r.data.days || [])).catch(() => {});
  }, []);

  if (!stats) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" data-testid="admin-dashboard-loading" /></div>;

  const cards = [
    { label: "Total Users", value: stats.total_users, icon: Users, testid: "stat-total-users" },
    { label: "Active Users", value: stats.active_users, icon: UserCheck, testid: "stat-active-users", accent: "text-green-600 bg-green-50" },
    { label: "Active Tasks", value: stats.active_tasks, icon: ClipboardList, testid: "stat-active-tasks" },
    { label: "Total Submissions", value: stats.total_submissions, icon: FileStack, testid: "stat-total-submissions" },
    { label: "Pending Verification", value: stats.pending_verification, icon: Hourglass, testid: "stat-pending-verification", accent: "text-amber-600 bg-amber-50" },
    { label: "Verified", value: stats.verified_submissions, icon: CheckCircle2, testid: "stat-verified-submissions", accent: "text-green-600 bg-green-50" },
    { label: "Rejected", value: stats.rejected_submissions, icon: XCircle, testid: "stat-rejected-submissions", accent: "text-red-600 bg-red-50" },
    { label: "Pending Rewards", value: inr(stats.pending_rewards), icon: Gift, testid: "stat-pending-rewards", accent: "text-indigo-600 bg-indigo-50" },
    { label: "Pending Withdrawals", value: stats.pending_withdrawals, icon: Banknote, testid: "stat-pending-withdrawals", accent: "text-amber-600 bg-amber-50" },
    { label: "Total Rewards Paid", value: inr(stats.total_rewards_paid), icon: IndianRupee, testid: "stat-total-rewards-paid", accent: "text-emerald-600 bg-emerald-50" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Admin Dashboard</h1>
      <p className="mt-1 text-sm text-slate-600">Platform-wide overview and analytics.</p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="admin-stats-grid">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid="admin-charts-grid">
        <ChartCard title="Daily Submissions (14 days)" testid="chart-daily-submissions">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip />
              <Area type="monotone" dataKey="submissions" stroke="#2563EB" fill="#DBEAFE" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Task Completions (verified)" testid="chart-task-completion">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip />
              <Bar dataKey="verified" fill="#10B981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Rewards Distributed (₹)" testid="chart-rewards-distributed">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={charts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip />
              <Area type="monotone" dataKey="rewards" stroke="#8B5CF6" fill="#EDE9FE" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="User Registrations" testid="chart-user-registrations">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip />
              <Bar dataKey="registrations" fill="#F59E0B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
